import Dexie from 'dexie';
import { accountIdentitySchema, accountIdentitySha256, accountSwitchIsReady, canonicalJsonStringify, datasetEpochSchema, decimalCursorSchema, deviceCursorSchema, EXPANDED_ROUTE_DRAFT_WIRE_GENERATION, evaluateLocalProfileAccess, instantSchema, localProfileSchema, profileWipeReceiptSchema, ROUTE_DRAFT_WIRE_WRITE_MODE, routeDraftWireGenerationOf, sha256HexUtf8, uuidSchema, } from '@der-maschinfuehrer/contracts';
import { DatasetEpochMismatchError, FullResyncRequiredError, ProfileAccessDeniedError, } from './errors.js';
import { LocalDataStore } from './repository.js';
import { createRuntime } from './runtime.js';
import { LOCAL_DB_SCHEMA_VERSION, PROFILE_INSTALLATION_SLOT, ProfileDatabase, ProfileDirectoryDatabase, physicalGenerationDatabaseName, } from './internal/database.js';
import { putValidatedDeviceCursor } from './internal/device-cursor.js';
import { defaultUserPreferences } from './internal/defaults.js';
import { entityRowFromPayload, parseEntityRowEnvelope } from './internal/entity.js';
import { replacementExecutionRowsMatch } from './internal/replacement-execution.js';
import { nextSnapshotMutationGeneration } from './internal/snapshot-generation.js';
import { assertProfileDatabaseMigrationFence, copyAndMigrateProfileDatabase, fenceProfileDatabaseForMigration, fenceProfileDatabaseForWipe, readProfileWithoutUpgrade, validateProfileDatabaseMigrationSource, WipeMutationGenerationChangedError, } from './internal/migration.js';
const ANONYMOUS_PROFILE_BOOTSTRAP_LOCK = 'dmf:local-profile-bootstrap';
export async function openLocalDataStore(options) {
    const suppliedProfile = localProfileSchema.parse(options.local_profile);
    const dependencies = options.dependencies ?? browserDependencies();
    const runtime = createRuntime(options.runtime);
    assertRequestedAccess(suppliedProfile, options.requested_account);
    const directory = new ProfileDirectoryDatabase(dependencies);
    await directory.open();
    let database = null;
    try {
        let entry = await directory.profiles.get(suppliedProfile.local_profile_id);
        if (entry === undefined) {
            const recovered = await recoverProfileBootstrap(suppliedProfile, directory, dependencies, runtime);
            if (recovered !== null) {
                entry = recovered.entry;
                database = recovered.database;
            }
        }
        if (entry === undefined) {
            const [occupiedProfile, occupiedBootstrap] = await Promise.all([
                directory.profiles.where('installation_slot').equals(PROFILE_INSTALLATION_SLOT).first(),
                directory.bootstrap_intents
                    .where('installation_slot')
                    .equals(PROFILE_INSTALLATION_SLOT)
                    .first(),
            ]);
            if (occupiedProfile !== undefined || occupiedBootstrap !== undefined) {
                throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
            }
            const initialized = await initializeProfileGeneration(suppliedProfile, options.device_cursor, directory, dependencies, runtime);
            entry = initialized.entry;
            database = initialized.database;
        }
        else {
            assertDirectoryBinding(entry, suppliedProfile);
            if (entry.schema_version < LOCAL_DB_SCHEMA_VERSION) {
                const migrated = await migrateProfileGeneration(entry, directory, dependencies, runtime);
                entry = migrated.entry;
                database = migrated.database;
            }
            else if (entry.schema_version === LOCAL_DB_SCHEMA_VERSION) {
                if (database === null) {
                    database = new ProfileDatabase(entry.active_database_name, dependencies);
                    await database.open();
                }
            }
            else {
                throw new Error('local database was created by a newer unsupported schema version');
            }
        }
        if (database === null) {
            throw new Error('active local database was not resolved');
        }
        let storedProfile = await readStoredProfile(database);
        assertStoredProfileMatchesDirectory(storedProfile, entry);
        if (entry.dataset_epoch !== storedProfile.dataset_epoch) {
            entry = await reconcileReplacementDirectory(directory, database, entry, storedProfile, runtime);
        }
        if (entry.profile_state !== storedProfile.profile_state) {
            entry = {
                ...entry,
                profile_state: storedProfile.profile_state,
                updated_at: runtime.now(),
            };
            await directory.profiles.put(entry);
        }
        assertRequestedAccess(storedProfile, options.requested_account);
        if (storedProfile.dataset_epoch !== suppliedProfile.dataset_epoch) {
            throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, suppliedProfile.dataset_epoch);
        }
        const cursor = options.device_cursor === undefined
            ? undefined
            : deviceCursorSchema.parse(options.device_cursor);
        storedProfile = await validateOpenGenerationAndMaybeStoreCursor(database, entry, storedProfile, cursor, runtime);
        await ensureCanonicalUserPreferences(database, storedProfile);
        await ensureRouteDraftWireGeneration(database, storedProfile);
        const currentEntry = await directory.profiles.get(storedProfile.local_profile_id);
        if (currentEntry === undefined ||
            currentEntry.active_database_name !== database.name ||
            currentEntry.generation !== entry.generation ||
            currentEntry.dataset_epoch !== storedProfile.dataset_epoch) {
            throw new ProfileAccessDeniedError('complete_wipe');
        }
        entry = currentEntry;
        const store = new LocalDataStore(database, directory, entry, storedProfile, runtime);
        database = null;
        return store;
    }
    catch (error) {
        database?.close();
        directory.close();
        throw error;
    }
}
export async function openOrCreateAnonymousLocalDataStore(options = {}) {
    const dependencies = options.dependencies ?? browserDependencies();
    const runtime = createRuntime(options.runtime);
    return dependencies.lock_manager.requestExclusive(ANONYMOUS_PROFILE_BOOTSTRAP_LOCK, async () => {
        const discovery = await discoverLocalDataStore({
            requested_account: null,
            dependencies,
            runtime,
        });
        if (discovery !== null) {
            return reopenLocalDataStore({
                local_profile_id: discovery.local_profile_id,
                requested_account: null,
                dependencies,
                runtime,
            });
        }
        const localProfileId = uuidSchema.parse(runtime.uuid());
        const datasetEpoch = uuidSchema.parse(runtime.uuid());
        const createdAt = instantSchema.parse(runtime.now());
        const profile = localProfileSchema.parse({
            contract_version: 'local_profile.v1',
            local_profile_id: localProfileId,
            database_binding: {
                local_profile_id: localProfileId,
                database_name: `dmf-profile-${localProfileId}`,
                account_identity_sha256: null,
                account_identity_digest_version: null,
            },
            created_at: createdAt,
            account_identity: null,
            profile_state: 'active',
            anonymous_transfer_state: 'none',
            dataset_epoch: datasetEpoch,
            indexeddb_schema_version: LOCAL_DB_SCHEMA_VERSION,
            last_mutation_at: null,
            last_successful_sync_at: null,
            last_verified_export: null,
        });
        return openLocalDataStore({
            local_profile: profile,
            requested_account: null,
            dependencies,
            runtime,
        });
    });
}
export async function discoverLocalDataStore(options) {
    const dependencies = options.dependencies ?? browserDependencies();
    const resolved = await loadDiscoverableProfile(null, options.requested_account, dependencies, createRuntime(options.runtime));
    if (resolved === null) {
        return null;
    }
    return {
        local_profile_id: resolved.profile.local_profile_id,
        logical_database_name: resolved.entry.logical_database_name,
        profile_state: resolved.profile.profile_state,
        dataset_epoch: resolved.profile.dataset_epoch,
        schema_version: resolved.entry.schema_version,
        recovery_state: resolved.recovered_bootstrap ? 'recovered_bootstrap' : 'ready',
    };
}
export async function reopenLocalDataStore(options) {
    const localProfileId = uuidSchema.parse(options.local_profile_id);
    const dependencies = options.dependencies ?? browserDependencies();
    const resolved = await loadDiscoverableProfile(localProfileId, options.requested_account, dependencies, createRuntime(options.runtime));
    if (resolved === null) {
        throw new ProfileAccessDeniedError('complete_wipe');
    }
    return openLocalDataStore({
        local_profile: resolved.profile,
        requested_account: options.requested_account,
        ...(options.device_cursor === undefined ? {} : { device_cursor: options.device_cursor }),
        dependencies,
        ...(options.runtime === undefined ? {} : { runtime: options.runtime }),
    });
}
export async function unlockLocalDataStoreForSameAccount(options) {
    const localProfileId = uuidSchema.parse(options.local_profile_id);
    const requestedAccount = accountIdentitySchema.parse(options.requested_account);
    const dependencies = options.dependencies ?? browserDependencies();
    const runtime = createRuntime(options.runtime);
    const directory = new ProfileDirectoryDatabase(dependencies);
    await directory.open();
    let database = null;
    try {
        let entry = await directory.profiles.get(localProfileId);
        if (entry === undefined ||
            entry.installation_slot !== PROFILE_INSTALLATION_SLOT ||
            entry.account_identity_sha256 !== accountIdentitySha256(requestedAccount)) {
            throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
        }
        if (entry.schema_version < LOCAL_DB_SCHEMA_VERSION) {
            const legacy = await readProfileWithoutUpgrade(entry.active_database_name, entry.schema_version, dependencies);
            assertStoredProfileMatchesDirectory(legacy.profile, entry);
            if (legacy.profile.account_identity === null ||
                legacy.profile.account_identity.issuer !== requestedAccount.issuer ||
                legacy.profile.account_identity.subject !== requestedAccount.subject) {
                throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
            }
            const migrated = await migrateProfileGeneration(entry, directory, dependencies, runtime);
            entry = migrated.entry;
            database = migrated.database;
        }
        else if (entry.schema_version === LOCAL_DB_SCHEMA_VERSION) {
            database = new ProfileDatabase(entry.active_database_name, dependencies);
            await database.open();
        }
        else {
            throw new Error('local database was created by a newer unsupported schema version');
        }
        const storedProfileRow = await database.profiles.get('profile');
        if (storedProfileRow === undefined) {
            throw new ProfileAccessDeniedError('complete_wipe');
        }
        const storedProfile = localProfileSchema.parse(storedProfileRow.value);
        assertStoredProfileMatchesDirectory(storedProfile, entry);
        if (storedProfile.account_identity === null ||
            storedProfile.account_identity.issuer !== requestedAccount.issuer ||
            storedProfile.account_identity.subject !== requestedAccount.subject) {
            throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
        }
        const runtimeState = await database.runtime_state.get('runtime');
        if (runtimeState === undefined ||
            runtimeState.write_fence !== 'open' ||
            runtimeState.physical_database_name !== database.name) {
            throw new ProfileAccessDeniedError('complete_wipe');
        }
        const activeProfile = localProfileSchema.parse({
            ...storedProfile,
            profile_state: 'active',
        });
        const profileChanged = storedProfile.profile_state !== 'active';
        const directoryChanged = entry.profile_state !== 'active';
        const activeEntry = directoryChanged
            ? {
                ...entry,
                profile_state: 'active',
                updated_at: runtime.now(),
            }
            : entry;
        const cursor = options.device_cursor === undefined
            ? undefined
            : deviceCursorSchema.parse(options.device_cursor);
        if (cursor !== undefined) {
            assertCursorMatchesProfile(cursor, activeProfile);
        }
        if (directoryChanged) {
            await directory.profiles.put(activeEntry);
        }
        const unlockedDatabase = database;
        await unlockedDatabase.transaction('rw', [unlockedDatabase.profiles, unlockedDatabase.device_cursors, unlockedDatabase.runtime_state], async () => {
            const currentRuntime = await unlockedDatabase.runtime_state.get('runtime');
            if (currentRuntime === undefined ||
                currentRuntime.local_profile_id !== activeProfile.local_profile_id ||
                currentRuntime.dataset_epoch !== activeProfile.dataset_epoch ||
                currentRuntime.write_fence !== 'open' ||
                currentRuntime.physical_database_name !== unlockedDatabase.name) {
                throw new FullResyncRequiredError('active local database generation changed during unlock');
            }
            if (profileChanged) {
                await unlockedDatabase.profiles.put({ key: 'profile', value: activeProfile });
            }
            let cursorChanged = false;
            if (cursor !== undefined) {
                cursorChanged = await putValidatedDeviceCursor(unlockedDatabase.device_cursors, cursor, activeProfile);
            }
            if (profileChanged || cursorChanged) {
                await unlockedDatabase.runtime_state.put({
                    ...currentRuntime,
                    mutation_generation: nextSnapshotMutationGeneration(currentRuntime),
                });
            }
        });
        await ensureRouteDraftWireGeneration(unlockedDatabase, activeProfile);
        const store = new LocalDataStore(unlockedDatabase, directory, activeEntry, activeProfile, runtime);
        database = null;
        return store;
    }
    catch (error) {
        database?.close();
        directory.close();
        throw error;
    }
}
export async function wipeLocalDataStoreForAccountSwitch(options) {
    if (!accountSwitchIsReady(options.preconditions)) {
        throw new Error('account switch preconditions are not complete');
    }
    const dependencies = options.dependencies ?? browserDependencies();
    const runtime = createRuntime(options.runtime);
    return dependencies.lock_manager.requestExclusive(profileStorageLockName(options.preconditions.source_local_profile_id), async () => wipeLocalDataStoreForAccountSwitchUnderLock(options, dependencies, runtime));
}
async function wipeLocalDataStoreForAccountSwitchUnderLock(options, dependencies, runtime) {
    const directory = new ProfileDirectoryDatabase(dependencies);
    await directory.open();
    try {
        let intent = await directory.wipe_intents.get(options.preconditions.source_local_profile_id);
        if (intent === undefined) {
            const entry = await directory.profiles.get(options.preconditions.source_local_profile_id);
            if (entry === undefined) {
                throw new Error('profile directory entry is missing');
            }
            if ((await directory.migration_intents.get(entry.local_profile_id)) !== undefined) {
                throw new Error('account-switch wipe cannot overlap a profile migration intent');
            }
            if (entry.schema_version !== LOCAL_DB_SCHEMA_VERSION) {
                throw new Error('profile must be migrated and validated before account-switch wipe');
            }
            const activeDatabase = new ProfileDatabase(entry.active_database_name, dependencies);
            await activeDatabase.open();
            const mutationGeneration = await (async () => {
                try {
                    return await activeDatabase.transaction('r', [activeDatabase.outbox, activeDatabase.profiles, activeDatabase.runtime_state], async () => {
                        if ((await activeDatabase.outbox.count()) !== 0) {
                            throw new Error('account switch refused because the persisted outbox is not empty');
                        }
                        const storedProfileRow = await activeDatabase.profiles.get('profile');
                        const storedProfile = localProfileSchema.parse(storedProfileRow?.value);
                        const runtimeState = await activeDatabase.runtime_state.get('runtime');
                        const expected = options.preconditions;
                        const verified = storedProfile.last_verified_export;
                        if (storedProfile.local_profile_id !== expected.source_local_profile_id ||
                            storedProfile.dataset_epoch !== expected.source_dataset_epoch ||
                            storedProfile.account_identity === null ||
                            storedProfile.account_identity.issuer !== expected.source_account_identity.issuer ||
                            storedProfile.account_identity.subject !==
                                expected.source_account_identity.subject ||
                            storedProfile.last_mutation_at !== expected.last_mutation_at ||
                            storedProfile.last_successful_sync_at !== expected.last_successful_sync_at ||
                            verified === null ||
                            verified.export_id !== expected.verified_export.export_id ||
                            verified.export_sha256 !== expected.verified_export.export_sha256 ||
                            verified.verified_at !== expected.verified_export.verified_at) {
                            throw new Error('persisted sync/export evidence does not match account-switch preconditions');
                        }
                        if (runtimeState?.mutation_generation === undefined ||
                            runtimeState.protected_mutation_generation === null ||
                            runtimeState.protected_mutation_generation === undefined ||
                            runtimeState.protected_mutation_generation !== runtimeState.mutation_generation) {
                            throw new Error('persisted mutation generation is not covered by sync/export evidence');
                        }
                        if (runtimeState.write_fence !== 'open' ||
                            runtimeState.physical_database_name !== entry.active_database_name) {
                            throw new Error('active profile database is already protected by a write fence');
                        }
                        return runtimeState.mutation_generation;
                    });
                }
                finally {
                    activeDatabase.close();
                }
            })();
            const bootstrapDatabaseNames = (await directory.bootstrap_intents
                .where('local_profile_id')
                .equals(entry.local_profile_id)
                .toArray()).map((bootstrap) => bootstrap.physical_database_name);
            const cacheStorageNames = canonicalCacheNames(await options.cache_storage.keys());
            intent = {
                local_profile_id: entry.local_profile_id,
                logical_database_name: entry.logical_database_name,
                active_database_name: entry.active_database_name,
                source_account_identity_sha256: accountIdentitySha256(options.preconditions.source_account_identity),
                target_account_identity_sha256: accountIdentitySha256(options.preconditions.target_account_identity),
                source_dataset_epoch: options.preconditions.source_dataset_epoch,
                last_mutation_at: options.preconditions.last_mutation_at,
                last_successful_sync_at: options.preconditions.last_successful_sync_at,
                verified_export_id: options.preconditions.verified_export.export_id,
                verified_export_sha256: options.preconditions.verified_export.export_sha256,
                verified_export_verified_at: options.preconditions.verified_export.verified_at,
                mutation_generation: mutationGeneration,
                write_fence_applied: false,
                database_names: [
                    ...new Set([
                        ...entry.staged_database_names,
                        ...entry.retired_database_names,
                        entry.active_database_name,
                        ...bootstrapDatabaseNames,
                    ]),
                ],
                cache_storage_names: cacheStorageNames,
                cache_storage_cleared_at: null,
                cache_storage_postcondition_sha256: null,
                created_at: runtime.now(),
            };
            await directory.wipe_intents.add(intent);
            runtime.fault?.('after_wipe_intent_persisted');
        }
        else if (!wipeIntentMatches(intent, options.preconditions)) {
            throw new Error('account-switch wipe retry does not match its persisted intent');
        }
        if (!intent.write_fence_applied) {
            try {
                await fenceProfileDatabaseForWipe(intent.active_database_name, intent.mutation_generation, dependencies);
            }
            catch (error) {
                const currentIntent = await directory.wipe_intents.get(intent.local_profile_id);
                if (currentIntent?.write_fence_applied === true) {
                    intent = currentIntent;
                }
                else {
                    if (error instanceof WipeMutationGenerationChangedError) {
                        await directory.wipe_intents.delete(intent.local_profile_id);
                    }
                    throw error;
                }
            }
            runtime.fault?.('after_wipe_write_fence');
            if (!intent.write_fence_applied) {
                const expectedIntent = intent;
                intent = await directory.transaction('rw', directory.wipe_intents, async () => {
                    const currentIntent = await directory.wipe_intents.get(expectedIntent.local_profile_id);
                    if (currentIntent === undefined) {
                        throw new Error('account-switch wipe intent disappeared before write fencing');
                    }
                    if (!wipeIntentRowsMatch(currentIntent, expectedIntent)) {
                        throw new Error('account-switch wipe intent changed before write fencing');
                    }
                    if (currentIntent.write_fence_applied) {
                        return currentIntent;
                    }
                    const fencedIntent = { ...currentIntent, write_fence_applied: true };
                    await directory.wipe_intents.put(fencedIntent);
                    return fencedIntent;
                });
            }
        }
        if (intent.cache_storage_cleared_at === null ||
            intent.cache_storage_postcondition_sha256 === null) {
            const clearedAt = runtime.now();
            const postconditionSha256 = await clearAndVerifyCacheStorage(options.cache_storage, intent.cache_storage_names);
            runtime.fault?.('after_cache_storage_cleared');
            const expectedIntent = intent;
            intent = await directory.transaction('rw', directory.wipe_intents, async () => {
                const currentIntent = await directory.wipe_intents.get(expectedIntent.local_profile_id);
                if (currentIntent === undefined || !wipeIntentRowsMatch(currentIntent, expectedIntent)) {
                    throw new Error('account-switch wipe intent changed during CacheStorage clearing');
                }
                const clearedIntent = {
                    ...currentIntent,
                    cache_storage_cleared_at: clearedAt,
                    cache_storage_postcondition_sha256: postconditionSha256,
                };
                await directory.wipe_intents.put(clearedIntent);
                return clearedIntent;
            });
        }
        else {
            const currentCacheNames = canonicalCacheNames(await options.cache_storage.keys());
            if (currentCacheNames.length !== 0) {
                throw new Error('CacheStorage was repopulated after its verified wipe');
            }
            if (intent.cache_storage_postcondition_sha256 !== cacheNamesSha256([])) {
                throw new Error('persisted CacheStorage wipe postcondition is invalid');
            }
        }
        for (const [index, databaseName] of intent.database_names.entries()) {
            const database = new Dexie(databaseName, {
                autoOpen: false,
                indexedDB: dependencies.indexedDB,
                IDBKeyRange: dependencies.IDBKeyRange,
            });
            await database.delete();
            if (index === 0) {
                runtime.fault?.('after_first_wipe_database_delete');
            }
        }
        await directory.transaction('rw', [
            directory.profiles,
            directory.bootstrap_intents,
            directory.replacement_checkpoints,
            directory.replacement_executions,
            directory.migration_intents,
            directory.wipe_intents,
        ], async () => {
            await directory.replacement_checkpoints
                .where('local_profile_id')
                .equals(intent.local_profile_id)
                .delete();
            await directory.replacement_executions
                .where('local_profile_id')
                .equals(intent.local_profile_id)
                .delete();
            await directory.bootstrap_intents.delete(intent.local_profile_id);
            await directory.migration_intents.delete(intent.local_profile_id);
            await directory.profiles.delete(intent.local_profile_id);
            await directory.wipe_intents.delete(intent.local_profile_id);
        });
        return profileWipeReceiptSchema.parse({
            wipe_receipt_id: runtime.uuid(),
            local_profile_id: intent.local_profile_id,
            database_name: intent.logical_database_name,
            cache_storage_cleared: true,
            indexeddb_deleted: true,
            completed_at: runtime.now(),
        });
    }
    finally {
        directory.close();
    }
}
function wipeIntentRowsMatch(left, right) {
    return (left.local_profile_id === right.local_profile_id &&
        left.logical_database_name === right.logical_database_name &&
        left.active_database_name === right.active_database_name &&
        left.source_account_identity_sha256 === right.source_account_identity_sha256 &&
        left.target_account_identity_sha256 === right.target_account_identity_sha256 &&
        left.source_dataset_epoch === right.source_dataset_epoch &&
        left.last_mutation_at === right.last_mutation_at &&
        left.last_successful_sync_at === right.last_successful_sync_at &&
        left.verified_export_id === right.verified_export_id &&
        left.verified_export_sha256 === right.verified_export_sha256 &&
        left.verified_export_verified_at === right.verified_export_verified_at &&
        left.mutation_generation === right.mutation_generation &&
        left.write_fence_applied === right.write_fence_applied &&
        left.created_at === right.created_at &&
        left.database_names.length === right.database_names.length &&
        left.database_names.every((databaseName, index) => right.database_names[index] === databaseName) &&
        left.cache_storage_names.length === right.cache_storage_names.length &&
        left.cache_storage_names.every((cacheName, index) => right.cache_storage_names[index] === cacheName) &&
        left.cache_storage_cleared_at === right.cache_storage_cleared_at &&
        left.cache_storage_postcondition_sha256 === right.cache_storage_postcondition_sha256);
}
function wipeIntentMatches(intent, preconditions) {
    return (intent.local_profile_id === preconditions.source_local_profile_id &&
        intent.source_account_identity_sha256 ===
            accountIdentitySha256(preconditions.source_account_identity) &&
        intent.target_account_identity_sha256 ===
            accountIdentitySha256(preconditions.target_account_identity) &&
        intent.source_dataset_epoch === preconditions.source_dataset_epoch &&
        intent.last_mutation_at === preconditions.last_mutation_at &&
        intent.last_successful_sync_at === preconditions.last_successful_sync_at &&
        intent.verified_export_id === preconditions.verified_export.export_id &&
        intent.verified_export_sha256 === preconditions.verified_export.export_sha256 &&
        intent.verified_export_verified_at === preconditions.verified_export.verified_at);
}
async function migrateProfileGeneration(sourceEntry, directory, dependencies, runtime) {
    return dependencies.lock_manager.requestExclusive(profileStorageLockName(sourceEntry.local_profile_id), async () => migrateProfileGenerationUnderLock(sourceEntry, directory, dependencies, runtime));
}
async function migrateProfileGenerationUnderLock(requestedSourceEntry, directory, dependencies, runtime) {
    const currentEntry = await directory.profiles.get(requestedSourceEntry.local_profile_id);
    if (currentEntry === undefined) {
        throw new Error('profile migration source disappeared before the exclusive lock');
    }
    if (currentEntry.schema_version === LOCAL_DB_SCHEMA_VERSION) {
        const database = new ProfileDatabase(currentEntry.active_database_name, dependencies);
        await database.open();
        return { entry: currentEntry, database };
    }
    if (currentEntry.schema_version > LOCAL_DB_SCHEMA_VERSION) {
        throw new Error('local database was created by a newer unsupported schema version');
    }
    await validateProfileDatabaseMigrationSource(currentEntry.active_database_name, currentEntry.schema_version, dependencies);
    const intent = await prepareProfileMigrationIntent(currentEntry, directory, runtime);
    const sourceMutationGeneration = await fenceProfileDatabaseForMigration(intent.source_database_name, intent.migration_id, dependencies);
    runtime.fault?.('after_migration_fence');
    const fencedIntent = await recordFencedMigrationIntent(intent, sourceMutationGeneration, directory, runtime);
    await resetOwnedMigrationTargets(fencedIntent, dependencies);
    let migrated = null;
    try {
        migrated = await copyAndMigrateProfileDatabase(fencedIntent.source_database_name, fencedIntent.source_schema_version, fencedIntent.targets.map((target) => target.database_name), fencedIntent.migration_id, dependencies, runtime);
        await assertProfileDatabaseMigrationFence(fencedIntent.source_database_name, fencedIntent.migration_id, sourceMutationGeneration, dependencies);
        runtime.fault?.('before_directory_activation');
        const finalTarget = fencedIntent.targets.at(-1);
        if (finalTarget === undefined || finalTarget.schema_version !== LOCAL_DB_SCHEMA_VERSION) {
            throw new Error('migration intent has no current-schema final target');
        }
        const targetNames = new Set(fencedIntent.targets.map((target) => target.database_name));
        const intermediateNames = fencedIntent.targets
            .slice(0, -1)
            .map((target) => target.database_name);
        const activatedEntry = await directory.transaction('rw', [directory.profiles, directory.migration_intents], async () => {
            const [current, persistedIntent] = await Promise.all([
                directory.profiles.get(fencedIntent.local_profile_id),
                directory.migration_intents.get(fencedIntent.local_profile_id),
            ]);
            if (current === undefined ||
                persistedIntent === undefined ||
                !migrationIntentRowsMatch(persistedIntent, fencedIntent) ||
                current.active_database_name !== fencedIntent.source_database_name ||
                current.schema_version !== fencedIntent.source_schema_version ||
                current.generation !== fencedIntent.source_generation ||
                !fencedIntent.targets.every((target) => current.staged_database_names.includes(target.database_name))) {
                throw new Error('profile migration source or durable intent changed before activation');
            }
            const activated = {
                ...current,
                active_database_name: finalTarget.database_name,
                retired_database_names: [
                    ...new Set([
                        ...current.retired_database_names,
                        fencedIntent.source_database_name,
                        ...intermediateNames,
                    ]),
                ],
                staged_database_names: current.staged_database_names.filter((databaseName) => !targetNames.has(databaseName)),
                schema_version: LOCAL_DB_SCHEMA_VERSION,
                generation: fencedIntent.target_generation,
                updated_at: runtime.now(),
            };
            await directory.profiles.put(activated);
            await directory.migration_intents.delete(fencedIntent.local_profile_id);
            return activated;
        });
        return { entry: activatedEntry, database: migrated.database };
    }
    catch (error) {
        migrated?.database.close();
        throw error;
    }
}
async function prepareProfileMigrationIntent(sourceEntry, directory, runtime) {
    return directory.transaction('rw', [directory.profiles, directory.migration_intents, directory.wipe_intents], async () => {
        const current = await directory.profiles.get(sourceEntry.local_profile_id);
        if (current === undefined ||
            current.active_database_name !== sourceEntry.active_database_name ||
            current.schema_version !== sourceEntry.schema_version ||
            current.generation !== sourceEntry.generation ||
            current.dataset_epoch !== sourceEntry.dataset_epoch) {
            throw new Error('profile migration source changed before intent preparation');
        }
        if ((await directory.wipe_intents.get(sourceEntry.local_profile_id)) !== undefined) {
            throw new Error('profile migration cannot overlap an account-switch wipe intent');
        }
        const existing = await directory.migration_intents.get(sourceEntry.local_profile_id);
        if (existing !== undefined) {
            if (!migrationIntentMatchesSource(existing, current)) {
                throw new Error('persisted migration intent belongs to another source generation');
            }
            return existing;
        }
        const migrationId = uuidSchema.parse(runtime.uuid());
        const targetGeneration = sourceEntry.generation + 1;
        const targets = Array.from({ length: LOCAL_DB_SCHEMA_VERSION - sourceEntry.schema_version }, (_, index) => {
            const schemaVersion = sourceEntry.schema_version + index + 1;
            return {
                schema_version: schemaVersion,
                database_name: physicalGenerationDatabaseName(sourceEntry.logical_database_name, targetGeneration, `${migrationId}-schema-${String(schemaVersion)}-${uuidSchema.parse(runtime.uuid())}`),
            };
        });
        if (targets.length === 0) {
            throw new Error('profile migration intent has no target steps');
        }
        const now = runtime.now();
        const intent = {
            local_profile_id: sourceEntry.local_profile_id,
            migration_id: migrationId,
            source_database_name: sourceEntry.active_database_name,
            source_schema_version: sourceEntry.schema_version,
            source_generation: sourceEntry.generation,
            source_dataset_epoch: sourceEntry.dataset_epoch,
            source_mutation_generation: null,
            target_generation: targetGeneration,
            targets,
            status: 'prepared',
            created_at: now,
            updated_at: now,
        };
        await directory.migration_intents.add(intent);
        await directory.profiles.put({
            ...current,
            staged_database_names: [
                ...new Set([
                    ...current.staged_database_names,
                    ...targets.map((target) => target.database_name),
                ]),
            ],
            updated_at: now,
        });
        return intent;
    });
}
async function recordFencedMigrationIntent(expectedIntent, sourceMutationGeneration, directory, runtime) {
    return directory.transaction('rw', directory.migration_intents, async () => {
        const current = await directory.migration_intents.get(expectedIntent.local_profile_id);
        if (current === undefined || !migrationIntentRowsMatch(current, expectedIntent)) {
            throw new Error('migration intent changed while its source fence was acquired');
        }
        if (current.status === 'fenced') {
            if (current.source_mutation_generation !== sourceMutationGeneration) {
                throw new Error('migration intent source generation differs from its owned fence');
            }
            return current;
        }
        const fenced = {
            ...current,
            source_mutation_generation: decimalCursorSchema.parse(sourceMutationGeneration),
            status: 'fenced',
            updated_at: runtime.now(),
        };
        await directory.migration_intents.put(fenced);
        return fenced;
    });
}
async function resetOwnedMigrationTargets(intent, dependencies) {
    if (intent.status !== 'fenced' || intent.source_mutation_generation === null) {
        throw new Error('migration targets cannot be rebuilt before the source fence is durable');
    }
    for (const target of intent.targets) {
        const database = new Dexie(target.database_name, {
            autoOpen: false,
            indexedDB: dependencies.indexedDB,
            IDBKeyRange: dependencies.IDBKeyRange,
        });
        await database.delete();
    }
}
function migrationIntentMatchesSource(intent, entry) {
    return (intent.local_profile_id === entry.local_profile_id &&
        intent.source_database_name === entry.active_database_name &&
        intent.source_schema_version === entry.schema_version &&
        intent.source_generation === entry.generation &&
        intent.source_dataset_epoch === entry.dataset_epoch &&
        intent.target_generation === entry.generation + 1 &&
        intent.targets.length === LOCAL_DB_SCHEMA_VERSION - entry.schema_version &&
        intent.targets.every((target, index) => target.schema_version === entry.schema_version + index + 1));
}
function migrationIntentRowsMatch(left, right) {
    return (left.local_profile_id === right.local_profile_id &&
        left.migration_id === right.migration_id &&
        left.source_database_name === right.source_database_name &&
        left.source_schema_version === right.source_schema_version &&
        left.source_generation === right.source_generation &&
        left.source_dataset_epoch === right.source_dataset_epoch &&
        left.source_mutation_generation === right.source_mutation_generation &&
        left.target_generation === right.target_generation &&
        left.status === right.status &&
        left.created_at === right.created_at &&
        left.updated_at === right.updated_at &&
        left.targets.length === right.targets.length &&
        left.targets.every((target, index) => target.schema_version === right.targets[index]?.schema_version &&
            target.database_name === right.targets[index]?.database_name));
}
function profileStorageLockName(localProfileId) {
    return `dmf-local-profile-storage:${localProfileId}`;
}
async function loadDiscoverableProfile(localProfileId, requestedAccount, dependencies, runtime) {
    const directory = new ProfileDirectoryDatabase(dependencies);
    await directory.open();
    let database = null;
    try {
        let entry = localProfileId === null
            ? await directory.profiles
                .where('installation_slot')
                .equals(PROFILE_INSTALLATION_SLOT)
                .first()
            : await directory.profiles.get(localProfileId);
        let recoveredBootstrap = false;
        if (entry === undefined) {
            const intent = localProfileId === null
                ? await directory.bootstrap_intents
                    .where('installation_slot')
                    .equals(PROFILE_INSTALLATION_SLOT)
                    .first()
                : await directory.bootstrap_intents.get(localProfileId);
            if (intent === undefined) {
                return null;
            }
            database = new ProfileDatabase(intent.physical_database_name, dependencies);
            await database.open();
            const pendingProfile = await readStoredProfile(database);
            assertRequestedAccess(pendingProfile, requestedAccount);
            database.close();
            database = null;
            const recovered = await recoverProfileBootstrap(pendingProfile, directory, dependencies, runtime);
            if (recovered === null) {
                throw new ProfileAccessDeniedError('complete_wipe');
            }
            entry = recovered.entry;
            database = recovered.database;
            recoveredBootstrap = true;
        }
        else if (entry.schema_version === LOCAL_DB_SCHEMA_VERSION) {
            database = new ProfileDatabase(entry.active_database_name, dependencies);
            await database.open();
        }
        else if (entry.schema_version > LOCAL_DB_SCHEMA_VERSION) {
            throw new Error('local database was created by a newer unsupported schema version');
        }
        const profile = database === null
            ? (await readProfileWithoutUpgrade(entry.active_database_name, entry.schema_version, dependencies)).profile
            : await readStoredProfile(database);
        assertStoredProfileMatchesDirectory(profile, entry);
        if (entry.dataset_epoch !== profile.dataset_epoch) {
            if (database === null) {
                throw new FullResyncRequiredError('legacy profile epoch differs from the directory before copy migration');
            }
            entry = await reconcileReplacementDirectory(directory, database, entry, profile, runtime);
        }
        assertRequestedAccess(profile, requestedAccount);
        return { profile, entry, recovered_bootstrap: recoveredBootstrap };
    }
    finally {
        database?.close();
        directory.close();
    }
}
async function recoverProfileBootstrap(suppliedProfile, directory, dependencies, runtime) {
    const intent = await directory.bootstrap_intents.get(suppliedProfile.local_profile_id);
    if (intent === undefined) {
        return null;
    }
    if (intent.logical_database_name !== suppliedProfile.database_binding.database_name ||
        intent.account_identity_sha256 !== suppliedProfile.database_binding.account_identity_sha256 ||
        intent.account_identity_digest_version !==
            suppliedProfile.database_binding.account_identity_digest_version ||
        intent.dataset_epoch !== suppliedProfile.dataset_epoch ||
        intent.schema_version !== LOCAL_DB_SCHEMA_VERSION ||
        intent.generation !== 1) {
        throw new ProfileAccessDeniedError('complete_wipe');
    }
    const database = new ProfileDatabase(intent.physical_database_name, dependencies);
    try {
        await database.open();
        const { profile, runtimeState } = await database.transaction('r', [database.profiles, database.runtime_state], async () => ({
            profile: await readStoredProfile(database),
            runtimeState: await database.runtime_state.get('runtime'),
        }));
        assertStoredProfileMatchesBootstrap(profile, intent);
        if (runtimeState === undefined ||
            runtimeState.local_profile_id !== profile.local_profile_id ||
            runtimeState.dataset_epoch !== profile.dataset_epoch ||
            runtimeState.write_fence !== 'open' ||
            runtimeState.physical_database_name !== intent.physical_database_name ||
            runtimeState.mutation_generation === undefined ||
            runtimeState.protected_mutation_generation === undefined) {
            throw new ProfileAccessDeniedError('complete_wipe');
        }
        decimalCursorSchema.parse(runtimeState.mutation_generation);
        if (runtimeState.protected_mutation_generation !== null) {
            decimalCursorSchema.parse(runtimeState.protected_mutation_generation);
        }
        const entry = {
            local_profile_id: profile.local_profile_id,
            installation_slot: PROFILE_INSTALLATION_SLOT,
            logical_database_name: intent.logical_database_name,
            active_database_name: intent.physical_database_name,
            retired_database_names: [],
            staged_database_names: [],
            account_identity_sha256: intent.account_identity_sha256,
            account_identity_digest_version: intent.account_identity_digest_version,
            profile_state: profile.profile_state,
            dataset_epoch: profile.dataset_epoch,
            schema_version: intent.schema_version,
            generation: intent.generation,
            updated_at: runtime.now(),
        };
        await directory.transaction('rw', [directory.profiles, directory.bootstrap_intents], async () => {
            const currentIntent = await directory.bootstrap_intents.get(profile.local_profile_id);
            if (!bootstrapIntentRowsMatch(currentIntent, intent)) {
                throw new Error('profile bootstrap intent changed during recovery');
            }
            const occupiedProfile = await directory.profiles
                .where('installation_slot')
                .equals(PROFILE_INSTALLATION_SLOT)
                .first();
            if (occupiedProfile !== undefined &&
                occupiedProfile.local_profile_id !== profile.local_profile_id) {
                throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
            }
            await directory.profiles.put(entry);
            await directory.bootstrap_intents.delete(profile.local_profile_id);
        });
        return { entry, database };
    }
    catch (error) {
        database.close();
        throw error;
    }
}
async function readStoredProfile(database) {
    const storedProfileRow = await database.profiles.get('profile');
    if (storedProfileRow === undefined) {
        throw new ProfileAccessDeniedError('complete_wipe');
    }
    return localProfileSchema.parse(storedProfileRow.value);
}
async function validateOpenGenerationAndMaybeStoreCursor(database, entry, expectedProfile, cursor, runtime) {
    return database.transaction('rw', [database.profiles, database.runtime_state, database.device_cursors], async () => {
        const storedProfile = await readStoredProfile(database);
        const runtimeState = await database.runtime_state.get('runtime');
        if (storedProfile.local_profile_id !== expectedProfile.local_profile_id ||
            storedProfile.dataset_epoch !== expectedProfile.dataset_epoch ||
            runtimeState === undefined ||
            runtimeState.local_profile_id !== storedProfile.local_profile_id ||
            runtimeState.dataset_epoch !== storedProfile.dataset_epoch ||
            runtimeState.write_fence !== 'open' ||
            runtimeState.physical_database_name !== database.name ||
            database.name !== entry.active_database_name ||
            runtimeState.mutation_generation === undefined ||
            runtimeState.protected_mutation_generation === undefined) {
            throw new FullResyncRequiredError('active local database generation changed during open');
        }
        decimalCursorSchema.parse(runtimeState.mutation_generation);
        if (runtimeState.protected_mutation_generation !== null) {
            decimalCursorSchema.parse(runtimeState.protected_mutation_generation);
        }
        if (cursor !== undefined) {
            assertCursorMatchesProfile(cursor, storedProfile);
            runtime.fault?.('before_open_cursor_write');
            const changed = await putValidatedDeviceCursor(database.device_cursors, cursor, storedProfile);
            if (changed) {
                await database.runtime_state.put({
                    ...runtimeState,
                    mutation_generation: nextSnapshotMutationGeneration(runtimeState),
                });
            }
        }
        return storedProfile;
    });
}
async function ensureCanonicalUserPreferences(database, expectedProfile) {
    await database.transaction('rw', [database.entities, database.profiles, database.runtime_state], async () => {
        const profile = await readStoredProfile(database);
        const runtime = await database.runtime_state.get('runtime');
        if (profile.local_profile_id !== expectedProfile.local_profile_id ||
            profile.dataset_epoch !== expectedProfile.dataset_epoch ||
            runtime === undefined ||
            runtime.local_profile_id !== profile.local_profile_id ||
            runtime.dataset_epoch !== profile.dataset_epoch ||
            runtime.write_fence !== 'open' ||
            runtime.physical_database_name !== database.name) {
            throw new FullResyncRequiredError('cannot initialize user preferences in an inactive local database generation');
        }
        const marker = runtime.replacement_commit ?? null;
        const datasetEpochRecord = datasetEpochSchema.parse(runtime.dataset_epoch_record ??
            (marker === null
                ? {
                    dataset_epoch: profile.dataset_epoch,
                    created_at: profile.created_at,
                    predecessor_epoch: null,
                    reason: 'initial',
                    restore_checkpoint_id: null,
                }
                : {
                    dataset_epoch: marker.replacement_epoch,
                    created_at: marker.committed_at,
                    predecessor_epoch: marker.predecessor_epoch,
                    reason: 'authoritative_replace',
                    restore_checkpoint_id: marker.restore_checkpoint_id,
                }));
        if (datasetEpochRecord.dataset_epoch !== profile.dataset_epoch ||
            (marker === null && datasetEpochRecord.reason !== 'initial') ||
            (marker !== null &&
                (datasetEpochRecord.reason !== 'authoritative_replace' ||
                    datasetEpochRecord.predecessor_epoch !== marker.predecessor_epoch ||
                    datasetEpochRecord.restore_checkpoint_id !== marker.restore_checkpoint_id ||
                    datasetEpochRecord.dataset_epoch !== marker.replacement_epoch))) {
            throw new FullResyncRequiredError('local dataset epoch provenance does not match its active profile generation');
        }
        const preferencesMarkerPresent = Object.hasOwn(runtime, 'preferences_initialized');
        if (preferencesMarkerPresent && runtime.preferences_initialized !== true) {
            throw new FullResyncRequiredError('local user-preferences initialization marker is invalid');
        }
        const rows = await database.entities
            .where('entity_type')
            .equals('user_preferences')
            .toArray();
        if (rows.length > 1) {
            throw new Error('local database contains multiple user-preferences entities');
        }
        let snapshotChanged = false;
        const existing = rows[0];
        if (existing !== undefined) {
            const canonical = parseEntityRowEnvelope(existing, profile.dataset_epoch);
            if (canonical.payload.entity_type !== 'user_preferences') {
                throw new Error('user-preferences index returned another entity type');
            }
        }
        else if (!preferencesMarkerPresent) {
            await database.entities.add(entityRowFromPayload({
                entity_type: 'user_preferences',
                value: defaultUserPreferences(profile),
            }, profile.dataset_epoch));
            snapshotChanged = true;
        }
        else {
            throw new FullResyncRequiredError('initialized local user preferences are missing from the active database generation');
        }
        if (runtime.dataset_epoch_record === undefined || runtime.preferences_initialized !== true) {
            await database.runtime_state.put({
                ...runtime,
                dataset_epoch_record: datasetEpochRecord,
                preferences_initialized: true,
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
        }
        else if (snapshotChanged) {
            await database.runtime_state.put({
                ...runtime,
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
        }
    });
}
async function ensureRouteDraftWireGeneration(database, expectedProfile) {
    await database.transaction('rw', [database.profiles, database.runtime_state], async () => {
        const profile = await readStoredProfile(database);
        const runtime = await database.runtime_state.get('runtime');
        if (profile.local_profile_id !== expectedProfile.local_profile_id ||
            profile.dataset_epoch !== expectedProfile.dataset_epoch ||
            runtime === undefined ||
            runtime.local_profile_id !== profile.local_profile_id ||
            runtime.dataset_epoch !== profile.dataset_epoch ||
            runtime.write_fence !== 'open' ||
            runtime.physical_database_name !== database.name ||
            runtime.mutation_generation === undefined) {
            throw new FullResyncRequiredError('cannot activate route draft wire generation in an inactive local database');
        }
        const marker = runtime.replacement_commit ?? null;
        const epoch = datasetEpochSchema.parse(runtime.dataset_epoch_record ??
            (marker === null
                ? {
                    dataset_epoch: profile.dataset_epoch,
                    created_at: profile.created_at,
                    predecessor_epoch: null,
                    reason: 'initial',
                    restore_checkpoint_id: null,
                }
                : {
                    dataset_epoch: marker.replacement_epoch,
                    created_at: marker.committed_at,
                    predecessor_epoch: marker.predecessor_epoch,
                    reason: 'authoritative_replace',
                    restore_checkpoint_id: marker.restore_checkpoint_id,
                }));
        if (epoch.dataset_epoch !== profile.dataset_epoch) {
            throw new FullResyncRequiredError('route draft wire generation belongs to another epoch');
        }
        if (ROUTE_DRAFT_WIRE_WRITE_MODE !== 'expanded_writer' ||
            routeDraftWireGenerationOf(epoch) === EXPANDED_ROUTE_DRAFT_WIRE_GENERATION) {
            return;
        }
        await database.runtime_state.put({
            ...runtime,
            dataset_epoch_record: {
                ...epoch,
                route_draft_wire_generation: EXPANDED_ROUTE_DRAFT_WIRE_GENERATION,
            },
            mutation_generation: nextSnapshotMutationGeneration(runtime),
        });
    });
}
async function reconcileReplacementDirectory(directory, database, entry, storedProfile, runtime) {
    const runtimeState = await database.runtime_state.get('runtime');
    const marker = runtimeState?.replacement_commit;
    const execution = marker === undefined || marker === null
        ? undefined
        : await directory.replacement_executions.get(marker.replacement_id);
    if (runtimeState === undefined ||
        marker === undefined ||
        marker === null ||
        execution === undefined ||
        runtimeState.physical_database_name !== database.name ||
        runtimeState.dataset_epoch !== storedProfile.dataset_epoch ||
        marker.predecessor_epoch !== entry.dataset_epoch ||
        marker.replacement_epoch !== storedProfile.dataset_epoch ||
        marker.restore_checkpoint_id !== execution.restore_checkpoint_id ||
        marker.restore_checkpoint_payload_sha256 !== execution.restore_checkpoint_payload_sha256 ||
        marker.execution_scope !== execution.execution_scope ||
        execution.local_profile_id !== storedProfile.local_profile_id ||
        execution.source_database_name !== database.name ||
        execution.source_generation !== entry.generation ||
        execution.predecessor_epoch !== entry.dataset_epoch ||
        execution.replacement_epoch !== storedProfile.dataset_epoch ||
        execution.status !== 'prepared' ||
        execution.applied_at !== null) {
        throw new FullResyncRequiredError('dataset epoch differs from the directory without exact replacement provenance');
    }
    const recoveryPause = runtime.pause?.('before_replacement_recovery_directory_transaction');
    if (recoveryPause !== undefined) {
        await recoveryPause;
    }
    return directory.transaction('rw', [directory.profiles, directory.replacement_executions], async () => {
        const currentEntry = await directory.profiles.get(entry.local_profile_id);
        const currentExecution = await directory.replacement_executions.get(execution.replacement_id);
        if (currentEntry === undefined ||
            currentExecution === undefined ||
            currentEntry.active_database_name !== database.name ||
            currentEntry.generation !== entry.generation ||
            currentEntry.dataset_epoch !== entry.dataset_epoch ||
            !replacementExecutionRowsMatch(currentExecution, execution)) {
            throw new FullResyncRequiredError('replacement recovery directory state changed');
        }
        const recoveredEntry = {
            ...currentEntry,
            dataset_epoch: storedProfile.dataset_epoch,
            updated_at: runtime.now(),
        };
        await directory.profiles.put(recoveredEntry);
        await directory.replacement_executions.put({
            ...currentExecution,
            status: 'applied',
            applied_at: currentExecution.applied_at ?? runtime.now(),
        });
        return recoveredEntry;
    });
}
function bootstrapIntentRowsMatch(left, right) {
    return (left !== undefined &&
        left.local_profile_id === right.local_profile_id &&
        left.installation_slot === right.installation_slot &&
        left.logical_database_name === right.logical_database_name &&
        left.physical_database_name === right.physical_database_name &&
        left.account_identity_sha256 === right.account_identity_sha256 &&
        left.account_identity_digest_version === right.account_identity_digest_version &&
        left.dataset_epoch === right.dataset_epoch &&
        left.schema_version === right.schema_version &&
        left.generation === right.generation &&
        left.created_at === right.created_at);
}
function assertStoredProfileMatchesBootstrap(profile, intent) {
    if (profile.local_profile_id !== intent.local_profile_id ||
        profile.database_binding.database_name !== intent.logical_database_name ||
        profile.database_binding.account_identity_sha256 !== intent.account_identity_sha256 ||
        profile.database_binding.account_identity_digest_version !==
            intent.account_identity_digest_version ||
        profile.dataset_epoch !== intent.dataset_epoch ||
        profile.indexeddb_schema_version !== intent.schema_version) {
        throw new ProfileAccessDeniedError('complete_wipe');
    }
}
async function initializeProfileGeneration(suppliedProfile, cursorInput, directory, dependencies, runtime) {
    const profile = localProfileSchema.parse({
        ...suppliedProfile,
        indexeddb_schema_version: LOCAL_DB_SCHEMA_VERSION,
    });
    const activeDatabaseName = physicalGenerationDatabaseName(profile.database_binding.database_name, 1, runtime.uuid());
    const intent = {
        local_profile_id: profile.local_profile_id,
        installation_slot: PROFILE_INSTALLATION_SLOT,
        logical_database_name: profile.database_binding.database_name,
        physical_database_name: activeDatabaseName,
        account_identity_sha256: profile.database_binding.account_identity_sha256,
        account_identity_digest_version: profile.database_binding.account_identity_digest_version,
        dataset_epoch: profile.dataset_epoch,
        schema_version: LOCAL_DB_SCHEMA_VERSION,
        generation: 1,
        created_at: runtime.now(),
    };
    await directory.transaction('rw', [directory.profiles, directory.bootstrap_intents], async () => {
        const [occupiedProfile, occupiedBootstrap] = await Promise.all([
            directory.profiles.where('installation_slot').equals(PROFILE_INSTALLATION_SLOT).first(),
            directory.bootstrap_intents
                .where('installation_slot')
                .equals(PROFILE_INSTALLATION_SLOT)
                .first(),
        ]);
        if (occupiedProfile !== undefined || occupiedBootstrap !== undefined) {
            throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
        }
        await directory.bootstrap_intents.add(intent);
    });
    const database = new ProfileDatabase(activeDatabaseName, dependencies);
    try {
        await database.open();
        const cursor = cursorInput === undefined ? undefined : deviceCursorSchema.parse(cursorInput);
        if (cursor !== undefined) {
            assertCursorMatchesProfile(cursor, profile);
        }
        const runtimeDeviceId = cursor?.device_id ?? runtime.uuid();
        await database.transaction('rw', [database.entities, database.profiles, database.runtime_state, database.device_cursors], async () => {
            await database.entities.add(entityRowFromPayload({
                entity_type: 'user_preferences',
                value: defaultUserPreferences(profile),
            }, profile.dataset_epoch));
            await database.profiles.add({ key: 'profile', value: profile });
            await database.runtime_state.add({
                key: 'runtime',
                local_profile_id: profile.local_profile_id,
                device_id: runtimeDeviceId,
                dataset_epoch: profile.dataset_epoch,
                dataset_epoch_record: {
                    dataset_epoch: profile.dataset_epoch,
                    created_at: profile.created_at,
                    predecessor_epoch: null,
                    reason: 'initial',
                    restore_checkpoint_id: null,
                },
                next_local_sequence: '1',
                write_fence: 'open',
                migration_fence_owner: null,
                migration_fence_source_generation: null,
                physical_database_name: activeDatabaseName,
                mutation_generation: '0',
                protected_mutation_generation: null,
                replacement_commit: null,
                preferences_initialized: true,
            });
            if (cursor !== undefined) {
                await putValidatedDeviceCursor(database.device_cursors, cursor, profile);
            }
        });
        const entry = {
            local_profile_id: profile.local_profile_id,
            installation_slot: PROFILE_INSTALLATION_SLOT,
            logical_database_name: profile.database_binding.database_name,
            active_database_name: activeDatabaseName,
            retired_database_names: [],
            staged_database_names: [],
            account_identity_sha256: profile.database_binding.account_identity_sha256,
            account_identity_digest_version: profile.database_binding.account_identity_digest_version,
            profile_state: profile.profile_state,
            dataset_epoch: profile.dataset_epoch,
            schema_version: LOCAL_DB_SCHEMA_VERSION,
            generation: 1,
            updated_at: runtime.now(),
        };
        runtime.fault?.('before_initial_directory_publish');
        await directory.transaction('rw', [directory.profiles, directory.bootstrap_intents], async () => {
            const currentIntent = await directory.bootstrap_intents.get(profile.local_profile_id);
            if (!bootstrapIntentRowsMatch(currentIntent, intent)) {
                throw new Error('profile bootstrap intent changed before directory publication');
            }
            const occupiedProfile = await directory.profiles
                .where('installation_slot')
                .equals(PROFILE_INSTALLATION_SLOT)
                .first();
            if (occupiedProfile !== undefined) {
                throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
            }
            await directory.profiles.add(entry);
            await directory.bootstrap_intents.delete(profile.local_profile_id);
        });
        return { entry, database };
    }
    catch (error) {
        database.close();
        throw error;
    }
}
function assertRequestedAccess(profile, requestedAccount) {
    const decision = evaluateLocalProfileAccess(profile, requestedAccount);
    if (!decision.allowed) {
        throw new ProfileAccessDeniedError(decision.required_action);
    }
}
function assertDirectoryBinding(entry, profile) {
    if (entry.logical_database_name !== profile.database_binding.database_name ||
        entry.account_identity_sha256 !== profile.database_binding.account_identity_sha256 ||
        entry.account_identity_digest_version !==
            profile.database_binding.account_identity_digest_version) {
        throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
    }
}
function assertStoredProfileMatchesDirectory(profile, entry) {
    if (profile.local_profile_id !== entry.local_profile_id ||
        profile.database_binding.database_name !== entry.logical_database_name ||
        profile.database_binding.account_identity_sha256 !== entry.account_identity_sha256 ||
        profile.database_binding.account_identity_digest_version !==
            entry.account_identity_digest_version) {
        throw new ProfileAccessDeniedError('complete_wipe');
    }
}
function assertCursorMatchesProfile(cursor, profile) {
    if (profile.account_identity === null) {
        throw new ProfileAccessDeniedError('explicit_anonymous_transfer');
    }
    if (cursor.account_identity.issuer !== profile.account_identity.issuer ||
        cursor.account_identity.subject !== profile.account_identity.subject ||
        cursor.dataset_epoch !== profile.dataset_epoch) {
        throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
    }
    if (profile.database_binding.account_identity_sha256 !==
        accountIdentitySha256(cursor.account_identity)) {
        throw new ProfileAccessDeniedError('complete_sync_export_and_wipe');
    }
}
function browserDependencies() {
    if (globalThis.indexedDB === undefined || globalThis.IDBKeyRange === undefined) {
        throw new Error('IndexedDB is unavailable; provide explicit local database dependencies');
    }
    return {
        indexedDB: globalThis.indexedDB,
        IDBKeyRange: globalThis.IDBKeyRange,
        lock_manager: {
            requestExclusive: async (name, operation) => {
                if (globalThis.navigator?.locks === undefined) {
                    throw new Error('Web Locks API is required for crash-safe local database migration');
                }
                return globalThis.navigator.locks.request(name, { mode: 'exclusive' }, operation);
            },
        },
    };
}
async function clearAndVerifyCacheStorage(cacheStorage, persistedCacheNames) {
    const currentCacheNames = canonicalCacheNames(await cacheStorage.keys());
    const cacheNames = canonicalCacheNames([...persistedCacheNames, ...currentCacheNames]);
    for (const cacheName of cacheNames) {
        await cacheStorage.delete(cacheName);
    }
    const remainingCacheNames = canonicalCacheNames(await cacheStorage.keys());
    if (remainingCacheNames.length !== 0) {
        throw new Error(`CacheStorage wipe postcondition failed for ${String(remainingCacheNames.length)} cache(s)`);
    }
    return cacheNamesSha256(remainingCacheNames);
}
function canonicalCacheNames(cacheNames) {
    const parsed = cacheNames.map((cacheName) => {
        if (typeof cacheName !== 'string' ||
            cacheName.length === 0 ||
            cacheName.length > 256 ||
            cacheName.trim() !== cacheName) {
            throw new Error('CacheStorage returned an invalid cache name');
        }
        return cacheName;
    });
    return [...new Set(parsed)].sort();
}
function cacheNamesSha256(cacheNames) {
    return sha256HexUtf8(`dmf.cache-storage-postcondition.sha256.v1\u0000${canonicalJsonStringify(canonicalCacheNames(cacheNames))}`);
}
//# sourceMappingURL=open.js.map